import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccountService } from '@frontendlib/common/services/account.service';
import { AuthService } from '@frontendlib/common/services/auth.service';
import {
  isValidWorkspaceAddressLocalPart,
  normalizeWorkspaceAddressLocalPart,
} from '@sharedlib/common/utils/workspace-address';
import {
  SpaceInvitationLocalPartAvailability,
  type SpaceInvitationResolutionDto,
} from '@sharedlib/dtos/dto/accounts/invitation-acceptance.dto';
import { SpaceInvitationRefusals } from '@sharedlib/dtos/dto/accounts/space-invitations.dto';
import CloseToken from '@modules/modal/types/modal-close.injection-token';
import { EMPTY, timer } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { InvitationAcceptanceApiService } from './invitation-acceptance-api.service';
import { spaceChatPath } from '@modules/common/utils/space-route';

type ModalStep = 'name' | 'address' | 'loading';
type Availability = 'idle' | 'pending' | 'available' | 'taken' | 'invalid' | 'network';

@Component({
  selector: 'app-join-invitation-modal',
  standalone: true,
  imports: [A11yModule, CommonModule, ReactiveFormsModule],
  templateUrl: './join-invitation-modal.component.html',
  styleUrl: './join-invitation-modal.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JoinInvitationModalComponent {
  private readonly api = inject(InvitationAcceptanceApiService);
  private readonly accounts = inject(AccountService);
  private readonly auth = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly close = inject(CloseToken);
  private readonly destroyRef = inject(DestroyRef);
  private readonly token = this.route.snapshot.paramMap.get('token') ?? '';
  protected readonly publicInvitationPath = `/join/${this.token}`;

  protected readonly invitation = signal<SpaceInvitationResolutionDto | null>(null);
  protected readonly step = signal<ModalStep>('name');
  protected readonly availability = signal<Availability>('idle');
  protected readonly error = signal<string | null>(null);
  protected readonly useSignupFailure = signal(false);
  protected readonly form = new FormGroup({
    firstName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    lastName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    teamEmailLocalPart: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
  });

  constructor() {
    this.api
      .resolve(this.token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (invitation) => this.invitation.set(invitation),
        error: () => this.error.set('We couldn’t load this invitation. Please try again.'),
      });
    this.accounts
      .getAccount(true)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((account) =>
        this.form.patchValue({ firstName: account.firstName ?? '', lastName: account.lastName ?? '' }),
      );

    this.form.controls.teamEmailLocalPart.valueChanges
      .pipe(
        map(normalizeWorkspaceAddressLocalPart),
        switchMap((localPart) => {
          this.form.controls.teamEmailLocalPart.setValue(localPart, { emitEvent: false });
          if (!isValidWorkspaceAddressLocalPart(localPart)) {
            this.availability.set(localPart ? 'invalid' : 'idle');
            return EMPTY;
          }
          this.availability.set('pending');
          return timer(300).pipe(
            switchMap(() => this.api.resolve(this.token, localPart)),
            map((result) => result.localPartAvailability),
            catchError(() => {
              this.availability.set('network');
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((availability) =>
        this.availability.set(
          availability === SpaceInvitationLocalPartAvailability.AVAILABLE
            ? 'available'
            : availability === SpaceInvitationLocalPartAvailability.TAKEN
              ? 'taken'
              : 'invalid',
        ),
      );
  }

  @HostListener('document:keydown.escape')
  protected escape(): void {
    if (this.step() !== 'loading') this.close.next();
  }

  protected next(): void {
    this.form.controls.firstName.markAsTouched();
    this.form.controls.lastName.markAsTouched();
    if (this.form.controls.firstName.valid && this.form.controls.lastName.valid) this.step.set('address');
  }

  protected accept(): void {
    if (this.step() === 'loading' || this.availability() !== 'available') return;
    this.step.set('loading');
    this.error.set(null);
    this.useSignupFailure.set(false);
    this.api
      .accept(this.token, this.form.getRawValue())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => void this.router.navigate(spaceChatPath(response.teamId)),
        error: (error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 403) {
            this.auth
              .signOut()
              .pipe(take(1), takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (): void => {
                  window.location.assign(`/auth/login?returnUrl=${encodeURIComponent(`/join/${this.token}/accept`)}`);
                },
                error: (): void => {
                  this.step.set('address');
                  this.error.set('Sign-out could not finish. Please try again.');
                },
              });
            return;
          }
          if (
            error instanceof HttpErrorResponse &&
            error.status === 409 &&
            this.errorMessage(error.error) === SpaceInvitationRefusals.USE_SIGNUP
          ) {
            this.step.set('address');
            this.useSignupFailure.set(true);
            this.error.set(
              "This team's invitations are accepted by creating a team account. Open your invitation link again to finish joining.",
            );
            return;
          }
          this.step.set('address');
          if (error instanceof HttpErrorResponse && error.status === 409) this.availability.set('taken');
          this.error.set('We couldn’t join the team. Please try again.');
        },
      });
  }

  protected dismiss(): void {
    this.close.next();
  }

  private errorMessage(body: unknown): string | null {
    if (typeof body === 'string') return body;
    if (!body || typeof body !== 'object') return null;
    const message = Reflect.get(body, 'message') as unknown;
    return typeof message === 'string' ? message : null;
  }
}
